import type React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import ElderlyRoundedIcon from '@mui/icons-material/ElderlyRounded';
import HouseRoundedIcon from '@mui/icons-material/HouseRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import useIsMobile from 'hooks/useIsMobile';
import Pusher from 'pusher-js';
import { setAccessToken } from 'redux/slices/accessToken.slice';
import {
  useCreateChatMessageMutation,
  useLazyGetChatUserMessagesQuery,
  useLazyGetUserChatIdentifierQuery,
  useCreateAnonymousChatMessageMutation,
} from 'services/api/chat.api';
import getReCaptchaToken from 'utils/getReCaptchaToken';

import styles from './LandingChat.module.scss';

const PUSHER_APP_KEY = import.meta.env.VITE_PUSHER_APP_KEY || '';

const financialOptions = [
  { text: 'Credito Hipotecario', icon: <HouseRoundedIcon /> },
  { text: 'Pension', icon: <ElderlyRoundedIcon /> },
  { text: 'Asesoria', icon: <SavingsRoundedIcon /> },
  { text: 'Productos Financieros', icon: <AttachMoneyRoundedIcon /> },
];

interface FinancialButtonsProps {
  onOptionClick: (option: string) => void;
  isCreatingUser: boolean;
}

const FinancialButtons = ({ onOptionClick, isCreatingUser }: FinancialButtonsProps) => {
  const { isMobile } = useIsMobile();

  return (
    <Box className={styles.financialButtons} sx={isMobile ? { flexDirection: 'column' } : undefined}>
      {financialOptions.map((option) => (
        <Button
          key={option.text}
          variant="outlined"
          size="small"
          onClick={() => onOptionClick(option.text)}
          startIcon={option.icon}
          disabled={isCreatingUser}
          className={styles.quickOptionButton}
          sx={isMobile ? { width: '100%' } : undefined}
        >
          {option.text}
        </Button>
      ))}
    </Box>
  );
};

const TypingDots = () => (
  <Box
    sx={{
      display: 'inline-flex',
      gap: '4px',
      alignItems: 'center',
      height: '20px',
    }}
  >
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: '6px',
          height: '6px',
          backgroundColor: 'text.primary',
          borderRadius: '50%',
          animation: 'typingAnimation 0.9s infinite',
          animationDelay: `${i * 0.2}s`,
          '@keyframes typingAnimation': {
            '0%, 100%': {
              transform: 'translateY(0)',
            },
            '50%': {
              transform: 'translateY(-4px)',
            },
          },
        }}
      />
    ))}
  </Box>
);

const ChatInterface = () => {
  const { isMobile } = useIsMobile();
  const [inputValue, setInputValue] = useState('');
  const [refetchFlag, setRefetchFlag] = useState(1);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const [isTyping, setIsTyping] = useState(true);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const welcomeMessage1 = {
    id: 'welcome-message-1',
    text: '¡Hola! 👋 Soy Nina, tu asesora financiera virtual 💫',
    isFromUser: false,
  };

  const welcomeMessage2 = {
    id: 'welcome-message-2',
    text: 'Selecciona una opción para comenzar!\n👨‍💼 Cotizar créditos\n👵 Simular pensión\n🏠 Simular Crédito Hipotecario\n📊 Asesoría financiera',
    isFromUser: false,
  };
  const dispatch = useDispatch();

  const [createChatMessage] = useCreateChatMessageMutation();
  const [getChatIdentifier, { data: userChatIdentifier }] = useLazyGetUserChatIdentifierQuery();
  const [getMessages, { data: messagesResponse }] = useLazyGetChatUserMessagesQuery();
  const [createAnonymousChatMessage] = useCreateAnonymousChatMessageMutation();

  // Transform messages data for rendering
  const messages = Array.isArray(messagesResponse) ? messagesResponse : [];

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // Effect for welcome message animation
  useEffect(() => {
    const typingTimer = setTimeout(() => {
      setIsTyping(false);
      setShowWelcomeMessage(true);
    }, 2000);

    return () => clearTimeout(typingTimer);
  }, []);

  // Effect to scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, showWelcomeMessage, isTyping]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !userChatIdentifier) return;

    try {
      await (createChatMessage as (arg: { message: string }) => Promise<unknown>)({ message: inputValue.trim() });
      setInputValue('');
      setRefetchFlag((prev) => prev + 1);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error sending message:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // eslint-disable-next-line no-void
      void handleSendMessage();
    }
  };

  const createAnonymousUser = async (initialMessage?: string) => {
    try {
      setIsCreatingUser(true);
      const recaptchaToken = await getReCaptchaToken();
      const { accessToken } = await createAnonymousChatMessage({
        isAnonymous: true,
        initialMessage,
        recaptchaToken,
      }).unwrap();

      // Store the token in Redux
      dispatch(setAccessToken({ accessToken }));

      // Wait a bit for Redux to update
      await new Promise((resolve) => setTimeout(resolve, 100)); // eslint-disable-line no-promise-executor-return

      // Now fetch the chat identifier and messages
      await (getChatIdentifier as (arg: undefined) => Promise<unknown>)(undefined);
      await (getMessages as (arg: undefined) => Promise<unknown>)(undefined);
      setRefetchFlag((prev) => prev + 1);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error creating anonymous user:', error);
    } finally {
      setIsCreatingUser(false);
    }
  };

  const safeHandleQuickOption = (option: string) => {
    if (!userChatIdentifier && !isCreatingUser) {
      createAnonymousUser(`Me gustaría información sobre ${option}`).catch(console.error); // eslint-disable-line no-console
      return;
    }

    const message = `Me gustaría información sobre ${option}`;
    (createChatMessage as (arg: { message: string }) => Promise<unknown>)({ message }).catch(console.error); // eslint-disable-line no-console
    setRefetchFlag((prev) => prev + 1);
  };

  // Effect to fetch messages
  useEffect(() => {
    if (userChatIdentifier) {
      const fetchMessages = async () => {
        try {
          await getMessages(undefined).unwrap();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error fetching messages:', error);
        }
      };
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchMessages();
    }
  }, [refetchFlag, getMessages, userChatIdentifier]);

  // Effect for Pusher
  useEffect(() => {
    if (!userChatIdentifier) return undefined;

    const pusher = new Pusher(PUSHER_APP_KEY, { cluster: 'sa1' });
    const channel = pusher.subscribe(`chat-${userChatIdentifier}`);

    channel.bind('new-message', () => {
      void setRefetchFlag((prev) => prev + 1); // eslint-disable-line no-void
    });

    // Enhanced cleanup function
    return () => {
      try {
        channel.unbind_all();
        channel.unsubscribe();
        pusher.unsubscribe(`chat-${userChatIdentifier}`);
        pusher.disconnect();
      } catch (error) {
        console.error('Error cleaning up Pusher:', error); // eslint-disable-line no-console
      }
    };
  }, [userChatIdentifier]);

  return (
    <Container
      maxWidth={false}
      sx={{
        height: isMobile ? '100vh' : '80vh',
        display: 'flex',
        flexDirection: 'column',
        p: isMobile ? 0 : undefined,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          borderRadius: isMobile ? 0 : '24px',
        }}
      >
        {/* Chat Area */}
        <Paper className={styles.chatContainer} elevation={0} ref={chatContainerRef}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
            {isTyping ? (
              <Paper
                className={`${styles.messageBubble} ${styles.assistant}`}
                sx={{
                  backgroundColor: 'grey.100',
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  maxWidth: 'fit-content',
                }}
              >
                <TypingDots />
              </Paper>
            ) : (
              showWelcomeMessage && (
                <>
                  <Paper
                    key={welcomeMessage1.id}
                    className={`${styles.messageBubble} ${styles.assistant}`}
                    sx={{
                      backgroundColor: 'grey.100',
                      color: 'text.primary',
                      maxWidth: 'fit-content',
                    }}
                  >
                    <Typography>{welcomeMessage1.text}</Typography>
                  </Paper>
                  <Paper
                    key={welcomeMessage2.id}
                    className={`${styles.messageBubble} ${styles.assistant}`}
                    sx={{
                      backgroundColor: 'grey.100',
                      color: 'text.primary',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    <Typography component="div" sx={{ textAlign: 'left' }}>
                      {welcomeMessage2.text}
                    </Typography>
                  </Paper>
                </>
              )
            )}
            {messages.map((message) => (
              <Paper
                key={message.id}
                className={`${styles.messageBubble} ${message.isFromUser ? styles.user : styles.assistant}`}
                sx={{
                  backgroundColor: 'grey.100',
                  color: 'text.primary',
                }}
              >
                <Typography>{message.text}</Typography>
              </Paper>
            ))}
          </Box>
        </Paper>

        {/* Input Area */}
        <Paper className={styles.inputContainer} elevation={3}>
          {messages.length === 0 ? (
            <Box>
              <Paper className={styles.emptyStateContainer}>
                <TextField
                  fullWidth
                  placeholder="Selecciona una opción para comenzar..."
                  disabled
                  size="small"
                  className={`${styles.textField} ${styles.disabled}`}
                />
                <Box sx={{ mt: 2 }}>
                  <FinancialButtons onOptionClick={safeHandleQuickOption} isCreatingUser={isCreatingUser} />
                </Box>
              </Paper>
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  placeholder="Escribe un mensaje..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={!userChatIdentifier}
                  size="small"
                  className={styles.textField}
                />
                <IconButton
                  color="primary"
                  onClick={() => void handleSendMessage()} // eslint-disable-line no-void
                  disabled={!userChatIdentifier || !inputValue.trim()}
                  className={styles.sendButton}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&.Mui-disabled': {
                      backgroundColor: 'action.disabledBackground',
                    },
                  }}
                >
                  <SendRoundedIcon />
                </IconButton>
              </Box>

              <Box className={styles.suggestionsSection}>
                <Typography variant="body2" color="text.secondary" className={styles.suggestionsTitle}>
                  Sugerencias:
                </Typography>
                <FinancialButtons onOptionClick={safeHandleQuickOption} isCreatingUser={isCreatingUser} />
              </Box>
            </Box>
          )}
        </Paper>
      </Paper>
    </Container>
  );
};

export default ChatInterface;
